import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../assets/logo.svg";
import logoCG from "../assets/Logo_CG_blackV.svg";
import moonIcon from "../assets/header/themes/moon.svg";
import sunIcon from "../assets/header/themes/sun.svg";
import flagEN from "../assets/header/flags/Us.svg";
import flagFR from "../assets/header/flags/Fr.svg";
import defaultAvatar from "../assets/default-avatar.png";
import { API_URL } from "../config";
import useBrowserDetect from "../hooks/useBrowserDetect";

const Icon = ({ name }) => {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  if (name === "search") {
    return (
      <svg {...common}>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4.2-4.2" />
      </svg>
    );
  }

  if (name === "users") {
    return (
      <svg {...common}>
        <path d="M16 20v-1.6c0-2.2-1.8-4-4-4H7c-2.2 0-4 1.8-4 4V20" />
        <circle cx="9.5" cy="7.5" r="3.5" />
        <path d="M21 20v-1.4c0-1.8-1.1-3.3-2.7-3.9" />
        <path d="M16.4 4.4a3.5 3.5 0 0 1 0 6.2" />
      </svg>
    );
  }

  if (name === "bolt") {
    return (
      <svg {...common}>
        <path d="M13 2 4.8 13.2h6.7L10.8 22 19 10.8h-6.7L13 2Z" />
      </svg>
    );
  }

  if (name === "calendar") {
    return (
      <svg {...common}>
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.4" />
        <path d="M8 3.5v4" />
        <path d="M16 3.5v4" />
        <path d="M3.5 10h17" />
      </svg>
    );
  }

  if (name === "chat") {
    return (
      <svg {...common}>
        <path d="M5 6.5h12a3 3 0 0 1 3 3v4.2a3 3 0 0 1-3 3h-6.6L5 20v-3.3a3 3 0 0 1-3-3V9.5a3 3 0 0 1 3-3Z" />
        <path d="M7.5 10.5h7" />
        <path d="M7.5 13.5h4.7" />
      </svg>
    );
  }

  if (name === "chevron") {
    return (
      <svg {...common}>
        <path d="m7 10 5 5 5-5" />
      </svg>
    );
  }

  return null;
};

const readSavedAvatar = () => {
  try {
    const saved = localStorage.getItem("userAvatar");
    return saved ? JSON.parse(saved) : defaultAvatar;
  } catch {
    return defaultAvatar;
  }
};

const SiteHeader = ({
  theme = "dark",
  lang = "en",
  allowFrench = true,
  onToggleTheme,
  onToggleLang,
  token,
  pendingMatchesCount = 0,
  isGuruDomain = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSamsungBrowser } = useBrowserDetect();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(readSavedAvatar);
  const [profileName, setProfileName] = useState("");

  const currentLogo = isGuruDomain ? logoCG : logo;
  const isDark = theme === "dark";
  const effectiveLang = allowFrench ? lang : "en";
  const t = effectiveLang === "fr"
    ? {
        search: "Rechercher personnes, événements...",
        invitations: "Invitations",
        matches: "Matchs",
        events: "Événements",
        chats: "Chats",
        myProfile: "Profil",
        login: "Se connecter",
        join: "Pas encore avec nous ?",
        register: "S'inscrire",
        profile: "Profil",
        theme: isDark ? "Mode clair" : "Mode sombre",
        language: "Langue",
      }
    : {
        search: "Search people, events...",
        invitations: "Invitations",
        matches: "Matches",
        events: "Events",
        chats: "Chats",
        myProfile: "Profile",
        login: "Sign In",
        join: "Not with us yet?",
        register: "Sign Up",
        profile: "Profile",
        theme: isDark ? "Light mode" : "Dark mode",
        language: "Language",
      };

  const languageOptions = [
    { value: "en", flag: flagEN, text: "EN" },
    ...(allowFrench ? [{ value: "fr", flag: flagFR, text: "FR" }] : []),
  ];
  const activeLanguage = languageOptions.find((option) => option.value === effectiveLang) || languageOptions[0];

  const navItems = [
    { key: "events", icon: "calendar", label: t.events, path: "/events" },
    { key: "chats", icon: "chat", label: t.chats, path: "/chat" },
  ];

  const displayProfileName = profileName || t.profile;
  const isProfileActive = location.pathname === "/profile";
  const isNavItemActive = (item) => (
    item.key === "chats"
      ? location.pathname === "/chat" || location.pathname.startsWith("/chat/")
      : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  );

  const openAuth = (target) => {
    navigate(target === "register" ? "/register" : "/auth");
  };

  const handleProfileMenuNavigate = (path, section) => {
    setIsProfileOpen(false);
    if (section) {
      navigate(path, {
        state: {
          profileSection: section,
          profileSectionTs: Date.now(),
        },
      });
      return;
    }
    navigate(path);
  };

  useEffect(() => {
    let cancelled = false;

    const loadProfileAvatar = async () => {
      if (!token) {
        setAvatarPreview(defaultAvatar);
        setProfileName("");
        return;
      }

      try {
        const { data } = await axios.get(`${API_URL}/api/profile_status`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000,
        });

        if (cancelled) return;
        setProfileName(data?.name || "");

        if (data?.id) {
          try {
            const avatarRes = await axios.get(`${API_URL}/api/avatar/${data.id}`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 3000,
            });

            if (cancelled) return;

            if (avatarRes.data?.has_custom_avatar && avatarRes.data?.direct_url) {
              const avatarUrl = avatarRes.data.direct_url;
              setAvatarPreview(avatarUrl);
              localStorage.setItem("userAvatar", JSON.stringify(avatarUrl));
              localStorage.setItem("userAvatarId", data.id.toString());
              localStorage.setItem("userAvatarTimestamp", Date.now().toString());
            } else {
              setAvatarPreview(defaultAvatar);
              localStorage.removeItem("userAvatar");
            }
          } catch {
            setAvatarPreview(readSavedAvatar());
          }
        } else {
          setAvatarPreview(defaultAvatar);
          localStorage.removeItem("userAvatar");
        }
      } catch {
        if (cancelled) return;
        setAvatarPreview(readSavedAvatar());
      }
    };

    loadProfileAvatar();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const headerCss = `
    .na-site-header,
    .na-site-header * {
      box-sizing: border-box;
    }

    .na-site-header {
      --na-line: rgba(154, 177, 255, 0.2);
      --na-soft-line: rgba(255, 255, 255, 0.12);
      --na-text: rgba(255, 255, 255, 0.94);
      --na-muted: rgba(230, 236, 255, 0.74);
      position: relative;
      isolation: isolate;
      width: 100%;
      min-height: 58px;
      border-radius: 10px;
      border: 1px solid rgba(121, 145, 210, 0.3);
      background:
        radial-gradient(circle at 8% 30%, rgba(46, 78, 142, 0.18), transparent 32%),
        radial-gradient(circle at 94% 42%, rgba(82, 48, 126, 0.2), transparent 36%),
        linear-gradient(98deg, rgba(18, 36, 82, 0.36) 0%, rgba(8, 14, 38, 0.46) 45%, rgba(50, 28, 86, 0.36) 100%);
      color: var(--na-text);
      display: flex;
      align-items: center;
      gap: clamp(10px, 1vw, 22px);
      padding: 0 clamp(12px, 2.25vw, 44px);
      overflow: visible;
      box-shadow:
        0 18px 48px rgba(5, 10, 34, 0.34),
        inset 0 1px 0 rgba(255, 255, 255, 0.2),
        inset 0 -1px 0 rgba(92, 55, 170, 0.28);
      backdrop-filter: blur(18px) saturate(190%) contrast(112%) brightness(108%);
      -webkit-backdrop-filter: blur(18px) saturate(190%) contrast(112%) brightness(108%);
    }

    .na-site-header::before,
    .na-site-header::after {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: inherit;
      pointer-events: none;
      z-index: 0;
    }

    .na-site-header::before {
      background:
        linear-gradient(112deg, rgba(255, 255, 255, 0.08), transparent 20% 76%, rgba(255, 255, 255, 0.05)),
        linear-gradient(90deg, rgba(55, 94, 168, 0.12), rgba(88, 71, 150, 0.08), rgba(112, 62, 150, 0.12));
      opacity: 0.46;
    }

    .na-site-header::after {
      inset: 1px;
      background:
        radial-gradient(90px 42px at 12% 22%, rgba(81, 126, 190, 0.13), transparent 72%),
        radial-gradient(120px 52px at 83% 44%, rgba(128, 77, 176, 0.14), transparent 72%),
        linear-gradient(100deg, transparent 12%, rgba(255, 255, 255, 0.04) 33%, transparent 47%, rgba(104, 96, 170, 0.06) 62%, transparent 82%);
      mix-blend-mode: screen;
      opacity: 0.42;
      filter: blur(0.5px);
    }

    .na-header-logo-btn,
    .na-header-item,
    .na-icon-toggle,
    .na-lang-toggle,
    .na-profile-menu-item,
    .na-profile-pill,
    .na-auth-btn {
      font: inherit;
      color: inherit;
      border: 0;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      position: relative;
      z-index: 1;
    }

    .na-header-logo-btn {
      width: clamp(112px, 7.4vw, 145px);
      height: 46px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      padding: 0;
      background: transparent;
    }

    .na-header-logo-btn img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .na-header-search {
      position: relative;
      z-index: 1;
      width: clamp(226px, 16vw, 312px);
      height: 42px;
      border-radius: 10px;
      border: 1px solid var(--na-soft-line);
      background: rgba(255, 255, 255, 0.07);
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0 14px 0 15px;
      flex: 0 1 312px;
      min-width: 210px;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
    }

    .na-header-search svg {
      width: 18px;
      height: 18px;
      color: rgba(229, 236, 255, 0.88);
      flex: 0 0 auto;
    }

    .na-header-search input {
      min-width: 0;
      flex: 1;
      border: 0;
      outline: 0;
      color: #ffffff;
      background: transparent;
      font: inherit;
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0;
    }

    .na-header-search input::placeholder {
      color: rgba(232, 237, 255, 0.76);
      opacity: 1;
    }

    .na-shortcut {
      color: rgba(232, 237, 255, 0.88);
      font-size: 12px;
      white-space: nowrap;
    }

    .na-header-nav {
      min-width: 0;
      flex: 1 1 auto;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      position: relative;
      z-index: 1;
    }

    .na-header-item {
      height: 42px;
      min-width: 132px;
      padding: 0 clamp(12px, 1.08vw, 22px);
      background: transparent;
      border-left: 1px solid var(--na-line);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      color: var(--na-text);
      transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
    }

    .na-header-item:last-child {
      border-right: 1px solid var(--na-line);
    }

    .na-header-item:hover {
      background: rgba(255, 255, 255, 0.06);
    }

    .na-header-item.active {
      background: linear-gradient(180deg, rgba(94, 85, 164, 0.2), rgba(58, 46, 112, 0.14));
      color: #ffffff;
      box-shadow:
        inset 0 0 0 1px rgba(155, 176, 230, 0.16),
        inset 0 -2px 0 rgba(104, 91, 170, 0.58),
        0 10px 24px rgba(42, 34, 96, 0.16);
    }

    .na-header-item.active svg {
      color: #ffffff;
      filter: drop-shadow(0 0 8px rgba(116, 100, 190, 0.38));
    }

    .na-header-item:active {
      transform: translateY(1px);
    }

    .na-header-item svg {
      width: 20px;
      height: 20px;
      flex: 0 0 auto;
      color: #ffffff;
      filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.28));
    }

    .na-header-label {
      font-size: 13px;
      line-height: 1;
      font-weight: 700;
      white-space: nowrap;
    }

    .na-header-actions {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-left: auto;
      flex: 0 0 auto;
      position: relative;
      z-index: 1;
    }

    .na-icon-toggle {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(8, 16, 60, 0.14);
      border: 1px solid rgba(130, 154, 255, 0.26);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
      transition: background 0.18s ease, border-color 0.18s ease;
    }

    .na-icon-toggle:hover {
      background: rgba(255, 255, 255, 0.07);
      border-color: rgba(160, 181, 255, 0.42);
    }

    .na-icon-toggle img {
      width: 20px;
      height: 20px;
      object-fit: contain;
      filter: brightness(0) invert(1);
    }

    .na-lang-wrap {
      position: relative;
      flex: 0 0 auto;
    }

    .na-lang-toggle {
      height: 36px;
      min-width: 66px;
      border-radius: 10px;
      background: rgba(38, 46, 105, 0.24);
      border: 1px solid rgba(130, 154, 255, 0.26);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      padding: 0 9px;
      transition: background 0.18s ease, border-color 0.18s ease;
    }

    .na-lang-toggle:hover {
      background: rgba(255, 255, 255, 0.07);
      border-color: rgba(160, 181, 255, 0.42);
    }

    .na-flag {
      width: 23px;
      height: 15px;
      border-radius: 3px;
      object-fit: cover;
      display: block;
      box-shadow: none;
    }

    .na-lang-toggle svg,
    .na-profile-pill svg {
      width: 14px;
      height: 14px;
      flex: 0 0 auto;
    }

    .na-lang-menu {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      width: 132px;
      padding: 7px;
      border-radius: 14px;
      border: 1px solid rgba(130, 154, 255, 0.28);
      background: rgba(16, 22, 62, 0.96);
      box-shadow: 0 18px 40px rgba(4, 8, 28, 0.45);
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
      z-index: 20;
    }

    .na-lang-option {
      width: 100%;
      height: 38px;
      border: 0;
      border-radius: 10px;
      background: transparent;
      color: var(--na-text);
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0 9px;
      cursor: pointer;
      font: inherit;
      font-size: 14px;
      font-weight: 700;
    }

    .na-lang-option:hover,
    .na-lang-option.active {
      background: rgba(117, 87, 255, 0.2);
    }

    .na-profile-pill {
      height: 44px;
      min-width: 184px;
      max-width: 214px;
      padding: 0 13px 0 10px;
      border-radius: 12px;
      background: rgba(97, 48, 173, 0.2);
      border: 1px solid rgba(130, 154, 255, 0.08);
      display: flex;
      align-items: center;
      gap: 10px;
      overflow: hidden;
      transition: background 0.18s ease, border-color 0.18s ease;
    }

    .na-profile-wrap {
      position: relative;
      flex: 0 0 auto;
    }

    .na-profile-pill:hover {
      background: rgba(116, 64, 198, 0.36);
      border-color: rgba(160, 181, 255, 0.28);
    }

    .na-profile-pill.active {
      background: linear-gradient(180deg, rgba(94, 85, 164, 0.24), rgba(70, 45, 112, 0.18));
      border-color: rgba(155, 176, 230, 0.18);
      box-shadow: inset 0 -2px 0 rgba(104, 91, 170, 0.58);
    }

    .na-avatar-wrap {
      position: relative;
      width: 34px;
      height: 34px;
      flex: 0 0 auto;
    }

    .na-avatar-wrap img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
      display: block;
      box-shadow: 0 8px 22px rgba(0, 0, 0, 0.32);
    }

    .na-profile-name {
      min-width: 0;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #ffffff;
      font-size: 13px;
      line-height: 1;
      font-weight: 800;
      text-align: left;
    }

    .na-profile-menu {
      position: fixed;
      top: 70px;
      right: 8px;
      width: min(218px, calc(100vw - 16px));
      padding: 7px;
      border-radius: 14px;
      border: 1px solid rgba(130, 154, 255, 0.28);
      background: rgba(16, 22, 62, 0.96);
      box-shadow: 0 18px 40px rgba(4, 8, 28, 0.45);
      backdrop-filter: blur(18px) saturate(160%);
      -webkit-backdrop-filter: blur(18px) saturate(160%);
      z-index: 2000;
    }

    .na-profile-menu-item {
      width: 100%;
      min-height: 40px;
      border-radius: 10px;
      background: transparent;
      color: var(--na-text);
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0 10px;
      font-size: 13px;
      font-weight: 750;
      text-align: left;
    }

    .na-profile-menu-item:hover {
      background: rgba(117, 87, 255, 0.2);
    }

    .na-profile-menu-item svg {
      width: 18px;
      height: 18px;
      flex: 0 0 auto;
    }

    .na-profile-menu-label {
      min-width: 0;
      flex: 1;
    }

    .na-menu-badge {
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      border-radius: 999px;
      background: #0abf30;
      color: #ffffff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      line-height: 1;
      font-weight: 900;
      box-shadow: 0 0 8px rgba(10, 191, 48, 0.48);
    }

    .na-auth-group {
      height: 44px;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 0;
      border-radius: 0;
      background: transparent;
      border: 0;
    }

    .na-auth-btn {
      height: 34px;
      border-radius: 999px;
      padding: 0 15px;
      background: transparent;
      border: 1px solid rgba(133, 157, 255, 0.84);
      color: #aebfff;
      font-size: 13px;
      font-weight: 500;
      white-space: nowrap;
    }

    .na-auth-copy {
      color: rgba(255, 255, 255, 0.9);
      font-size: 13px;
      font-weight: 500;
      white-space: nowrap;
    }

    .na-auth-btn.primary {
      min-width: 98px;
      color: #ffffff;
      font-weight: 750;
      border: 0;
      background: linear-gradient(105deg, #239c98 0%, #36aba7 36%, #6d55c8 72%, #8253c7 100%);
      box-shadow: 0 8px 20px rgba(60, 62, 150, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.18);
    }

    @media (max-width: 1580px) {
      .na-site-header {
        gap: 9px;
        padding: 0 14px;
      }

      .na-header-logo-btn {
        width: 110px;
      }

      .na-header-search {
        width: 220px;
        min-width: 185px;
      }

      .na-header-item {
        min-width: 112px;
        padding: 0 10px;
      }

      .na-header-label {
        font-size: 14px;
      }

      .na-profile-pill {
        min-width: 168px;
      }
    }

    @media (max-width: 1260px) {
      .na-header-search {
        width: 188px;
        min-width: 168px;
      }

      .na-shortcut {
        display: none;
      }

      .na-header-item {
        min-width: 54px;
        padding: 0 9px;
      }

      .na-header-label {
        display: none;
      }

      .na-profile-pill {
        min-width: 50px;
        max-width: 50px;
        padding: 0 8px;
      }

      .na-profile-name,
      .na-profile-pill svg {
        display: none;
      }
    }
  `;

  return (
    <>
      <style>{headerCss}</style>
      <header className="na-site-header">
        <button
          type="button"
          className="na-header-logo-btn"
          onClick={() => navigate("/")}
          aria-label="Home"
        >
          <img src={currentLogo} alt="Logo" />
        </button>

        {/*
        <form className="na-header-search" onSubmit={handleSearchSubmit}>
          <Icon name="search" />
          <input
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder={t.search}
            aria-label={t.search}
          />
          <span className="na-shortcut">⌘K</span>
        </form>
        */}

        {token && (
          <nav className="na-header-nav" aria-label="Primary">
            {navItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`na-header-item ${isNavItemActive(item) ? "active" : ""}`}
                onClick={() => navigate(item.path)}
              >
                <Icon name={item.icon} />
                <span className="na-header-label">{item.label}</span>
              </button>
            ))}
          </nav>
        )}

        <div className="na-header-actions">
          {!isSamsungBrowser && (
            <button
              type="button"
              className="na-icon-toggle"
              onClick={onToggleTheme}
              aria-label={t.theme}
              title={t.theme}
            >
              <img src={isDark ? sunIcon : moonIcon} alt="" />
            </button>
          )}

          {!isGuruDomain && (
            <div className="na-lang-wrap">
              <button
                type="button"
                className="na-lang-toggle"
                onClick={() => {
                  setIsProfileOpen(false);
                  setIsLangOpen((value) => !value);
                }}
                aria-label={t.language}
                aria-expanded={isLangOpen}
              >
                <img className="na-flag" src={activeLanguage.flag} alt={activeLanguage.text} />
                <Icon name="chevron" />
              </button>

              {isLangOpen && (
                <div className="na-lang-menu">
                  {languageOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`na-lang-option ${option.value === effectiveLang ? "active" : ""}`}
                      onClick={() => {
                        if (option.value !== effectiveLang) onToggleLang(option.value);
                        setIsLangOpen(false);
                      }}
                    >
                      <img className="na-flag" src={option.flag} alt="" />
                      {option.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {token ? (
            <div className="na-profile-wrap">
              <button
                type="button"
                className={`na-profile-pill ${isProfileActive ? "active" : ""}`}
                onClick={() => {
                  setIsLangOpen(false);
                  setIsProfileOpen((value) => !value);
                }}
                aria-expanded={isProfileOpen}
              >
                <span className="na-avatar-wrap">
                  <img src={avatarPreview || defaultAvatar} alt="" />
                </span>
                <span className="na-profile-name">{displayProfileName}</span>
                <Icon name="chevron" />
              </button>

              {isProfileOpen && (
                <div className="na-profile-menu">
                  <button type="button" className="na-profile-menu-item" onClick={() => handleProfileMenuNavigate("/profile")}>
                    <Icon name="users" />
                    {t.myProfile}
                  </button>
                  <button type="button" className="na-profile-menu-item" onClick={() => handleProfileMenuNavigate("/profile?section=invites", "invites")}>
                    <Icon name="users" />
                    {t.invitations}
                  </button>
                  <button type="button" className="na-profile-menu-item" onClick={() => handleProfileMenuNavigate("/profile?section=matches", "matches")}>
                    <Icon name="bolt" />
                    <span className="na-profile-menu-label">{t.matches}</span>
                    {pendingMatchesCount > 0 && (
                      <span className="na-menu-badge" aria-label={`${pendingMatchesCount} pending matches`}>
                        {pendingMatchesCount > 99 ? "99+" : pendingMatchesCount}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="na-auth-group">
              <button type="button" className="na-auth-btn" onClick={() => openAuth("auth")}>
                {t.login}
              </button>
              <span className="na-auth-copy">{t.join}</span>
              <button type="button" className="na-auth-btn primary" onClick={() => openAuth("register")}>
                {t.register}
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default SiteHeader;
